/**
 * Catálogo dos campos do formulário de orçamento (BDM-971).
 *
 * A ordem e as chaves espelham `App\Support\BudgetFormConfig` no backend — o
 * `PUT /budget-form` recusa chave que ele não conheça, então divergir aqui
 * quebra o save, não só a tela.
 *
 * `locked` marca o que o tenant não pode afrouxar — hoje só o nome, porque um
 * orçamento sem nome não é atendível. O e-mail deixou de ser travado na
 * BDM-974: sem ele o cliente é identificado pelo telefone e a proposta
 * automática não é enviada, o que a tela avisa antes de salvar.
 */
export const REQUIRED = 'required';
export const OPTIONAL = 'optional';
export const HIDDEN = 'hidden';

export const STATES = [
    { value: REQUIRED, label: 'Obrigatório' },
    { value: OPTIONAL, label: 'Opcional' },
    { value: HIDDEN, label: 'Não pedir' },
];

export const STATE_LABEL = {
    [REQUIRED]: 'obrigatório',
    [OPTIONAL]: 'opcional',
};

export const COMPANY_FIELDS = ['trade_name', 'cnpj'];

export const FIELDS = [
    {
        key: 'customer_name',
        name: 'Nome da pessoa',
        description: 'Quem está pedindo o orçamento.',
        placeholder: 'Seu nome',
        locked: true,
        lockReason: 'Sem o nome de quem pediu não há como atender o orçamento.',
    },
    {
        key: 'customer_phone',
        name: 'Telefone',
        description: 'Costuma ser o canal mais rápido de resposta.',
        placeholder: '(11) 90000-0000',
    },
    {
        key: 'customer_email',
        name: 'E-mail',
        description: 'Usado para enviar a proposta e reconhecer clientes que já compraram.',
        placeholder: 'voce@empresa.com.br',
    },
    {
        key: 'trade_name',
        name: 'Nome da empresa',
        description: 'Aparece só para quem escolhe "Pessoa jurídica".',
        placeholder: 'Nome da empresa',
        company: true,
    },
    {
        key: 'cnpj',
        name: 'CNPJ',
        description: 'Validado automaticamente. Aparece só para empresas.',
        placeholder: '00.000.000/0000-00',
        company: true,
    },
    {
        key: 'comments',
        name: 'Observações',
        description: 'Espaço livre para o cliente detalhar o pedido.',
        placeholder: 'Conte o que você precisa',
        wide: true,
    },
];

// Sem descrição: a prévia ao lado mostra o efeito de cada opção na hora do
// clique, e é explicação melhor do que texto no card.
export const AUDIENCES = [
    { key: 'both', personTypes: ['PJ', 'PF'], icon: 'people', title: 'Empresas e pessoas' },
    { key: 'pj', personTypes: ['PJ'], icon: 'company', title: 'Só empresas' },
    { key: 'pf', personTypes: ['PF'], icon: 'user', title: 'Só pessoas físicas' },
];

/**
 * Estado inicial dos campos, e também o formulário anterior à feature: espelha
 * `BudgetFormConfig::defaults()` no backend, que é o que um tenant sem config
 * gravada recebe.
 */
export const DEFAULT_FIELDS = {
    customer_name: REQUIRED,
    customer_phone: REQUIRED,
    customer_email: REQUIRED,
    trade_name: REQUIRED,
    cnpj: OPTIONAL,
    comments: OPTIONAL,
};

export const audienceFromPersonTypes = personTypes => {
    const list = Array.isArray(personTypes) ? personTypes : [];
    const found = AUDIENCES.find(audience =>
        audience.personTypes.length === list.length
        && audience.personTypes.every(type => list.includes(type))
    );

    return found ? found.key : 'both';
};

export const hasPj = personTypes => (personTypes ?? []).includes('PJ');
export const hasPf = personTypes => (personTypes ?? []).includes('PF');
